import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, ArrowRight, Award, ClipboardCheck } from "lucide-react";
import { Link } from "react-router-dom";

const CyberEssentialsService = () => {
  const localAreas = ["Somerset", "North Somerset", "Weston-super-Mare", "Bristol", "South Gloucestershire"];
  
  return (
    <div className="min-h-screen">
      <SEO
        title="Cyber Essentials Services | Certification & Readiness | 4FOX Security"
        description="Cyber Essentials certification and readiness services for UK businesses. 98% first-time pass rate. Get certified or check your readiness. Somerset, Bristol & nationwide."
        canonical="https://4fox.uk/services/cyber-essentials"
        localAreas={localAreas}
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Cyber Essentials Services",
          "provider": {
            "@type": "Organization",
            "name": "4FOX Security"
          },
          "serviceType": "Cyber Essentials Certification and Readiness",
          "areaServed": [
            { "@type": "Country", "name": "United Kingdom" },
            ...localAreas.map(area => ({ "@type": "City", "name": area }))
          ]
        }}
      />
      <Navbar />
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-12 h-12 text-accent" />
              <h1 className="text-4xl md:text-5xl font-bold">Cyber Essentials Services</h1>
            </div>
            
            <p className="text-xl text-muted-foreground mb-8">
              Expert support for Cyber Essentials certification and readiness. 98% first-time pass rate for Somerset, Bristol, and UK businesses.
            </p>

            {/* Trust Signals */}
            <div className="grid md:grid-cols-3 gap-4 mb-12">
              <div className="bg-accent/10 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-accent">98%</div>
                <div className="text-sm text-muted-foreground">First-Time Pass Rate</div>
              </div>
              <div className="bg-accent/10 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-accent">100+</div>
                <div className="text-sm text-muted-foreground">Businesses Certified</div>
              </div>
              <div className="bg-accent/10 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-accent">4-6</div>
                <div className="text-sm text-muted-foreground">Weeks to Certification</div>
              </div>
            </div>

            {/* What is Cyber Essentials */}
            <div className="bg-primary/5 border-l-4 border-accent rounded-lg p-6 mb-12">
              <h2 className="text-2xl font-bold mb-3">What is Cyber Essentials?</h2>
              <p className="text-muted-foreground mb-3">
                Cyber Essentials is a UK government-backed certification scheme that helps organizations protect themselves against common cyber threats. It's increasingly required for government contracts and demonstrates to clients and partners that you take cyber security seriously.
              </p>
              <p className="text-muted-foreground">
                The scheme covers five key technical controls that, when properly implemented, can prevent approximately 80% of common cyber attacks.
              </p>
            </div>

            {/* Two Service Cards */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6 text-center">Choose Your Path</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Certification Card */}
                <Link to="/services/cyber-essentials-certification-uk">
                  <Card className="group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-accent hover:border-2 cursor-pointer h-full">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-muted text-muted-foreground group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                        <Award className="w-6 h-6" />
                      </div>
                      <CardTitle className="text-xl">Cyber Essentials Certification</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base mb-4">
                        Full certification support from gap analysis to certification. Required for UK government contracts.
                      </CardDescription>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                          <span>98% first-time pass rate</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                          <span>Certified in 4-6 weeks</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                          <span>Hands-on remediation support</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                          <span>Application & submission included</span>
                        </li>
                      </ul>
                      <div className="mt-4">
                        <Button className="w-full bg-accent hover:bg-accent/90">
                          Get Certified
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>

                {/* Readiness Card */}
                <Link to="/services/cyber-essentials-readiness">
                  <Card className="group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-accent hover:border-2 cursor-pointer h-full">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-muted text-muted-foreground group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                        <ClipboardCheck className="w-6 h-6" />
                      </div>
                      <CardTitle className="text-xl">Cyber Essentials Readiness</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base mb-4">
                        Check if you're ready to certify. Free initial consultation to identify gaps and create your roadmap.
                      </CardDescription>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                          <span>Free 30-minute consultation</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                          <span>Gap analysis across 5 controls</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                          <span>Prioritized remediation roadmap</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                          <span>Clear path to certification</span>
                        </li>
                      </ul>
                      <div className="mt-4">
                        <Button variant="outline" className="w-full">
                          Check Readiness
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </div>

            {/* Why Cyber Essentials Matters */}
            <div className="prose prose-lg max-w-none mb-12">
              <h2 className="text-2xl font-bold mb-4">Why Cyber Essentials Matters</h2>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>Required for UK government contracts</strong>
                    <p className="text-muted-foreground text-sm">Mandatory for contracts handling sensitive data</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>Cyber insurance discounts</strong>
                    <p className="text-muted-foreground text-sm">10-15% reduction in premiums reported</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>Competitive advantage</strong>
                    <p className="text-muted-foreground text-sm">Win more bids with security credentials</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>Customer confidence</strong>
                    <p className="text-muted-foreground text-sm">Demonstrate commitment to security</p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold mb-4">The 5 Cyber Essentials Controls</h2>
              <div className="space-y-3 mb-6">
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong className="text-accent">1. Firewalls</strong>
                  <p className="text-muted-foreground text-sm mt-1">Configure and document network security boundaries</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong className="text-accent">2. Secure Configuration</strong>
                  <p className="text-muted-foreground text-sm mt-1">Harden systems and remove unnecessary services</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong className="text-accent">3. User Access Control</strong>
                  <p className="text-muted-foreground text-sm mt-1">Implement proper authentication and authorization</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong className="text-accent">4. Malware Protection</strong>
                  <p className="text-muted-foreground text-sm mt-1">Deploy and maintain anti-malware solutions</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong className="text-accent">5. Patch Management</strong>
                  <p className="text-muted-foreground text-sm mt-1">Ensure timely security updates across all systems</p>
                </div>
              </div>

              <div className="bg-accent/10 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-2">Serving Somerset, Bristol & Beyond</h3>
                <p className="text-muted-foreground">
                  We support businesses across Somerset, North Somerset, Weston-super-Mare, Bristol, South Gloucestershire, and throughout the UK with expert Cyber Essentials services.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CyberEssentialsService;
